import { z } from 'zod'
import { decimalStringSchema, timestampSchema, versionedRecordSchema } from './records.ts'

const id = z.string().min(1).max(200)
const positive = decimalStringSchema.refine(
  (value) => /[1-9]/.test(value) && !value.startsWith('-'),
)
export const engineeringSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('manual') }).strict(),
  z
    .object({ kind: z.literal('monthly'), rowId: id, month: z.string(), label: z.string() })
    .strict(),
  z.object({ kind: z.literal('order') }).strict(),
])
export const engineeringCreateSchema = z
  .object({
    productId: id.optional(),
    orderId: id.optional(),
    orderVersion: z.number().int().positive().optional(),
    name: z.string().trim().min(1).max(500),
    quantity: positive,
    unit: z.string().trim().min(1).max(40),
    engineerId: id.nullable(),
    /** Other engineers on the same task besides the responsible one. */
    helperIds: z.array(id).max(20).optional(),
    dueAt: timestampSchema,
    priority: z.enum(['low', 'medium', 'high']),
    note: z.string().max(20000),
    source: engineeringSourceSchema,
  })
  .strict()
export const engineeringBomLineSchema = z
  .object({
    id,
    materialId: id,
    perUnit: positive,
    waste: positive.refine((value) => /[1-9]/.test(value.split('.')[0] ?? '')),
    stageKey: z.string().max(200),
  })
  .strict()
export const engineeringEditSchema = versionedRecordSchema
  .extend({
    route: z.array(id).max(100).optional(),
    bom: z.array(engineeringBomLineSchema).max(500).optional(),
    instructions: z.string().max(100000).optional(),
  })
  .strict()
export const engineeringAssignSchema = versionedRecordSchema
  .extend({ engineerId: id, helperIds: z.array(id).max(20).optional() })
  .strict()
export const engineeringFileSchema = versionedRecordSchema
  .extend({
    slot: z.enum(['drawing', 'other', 'media']),
  })
  .strict()
export const engineeringRemoveMediaSchema = versionedRecordSchema.extend({ fileId: id }).strict()
export const engineeringReworkSchema = z
  .object({
    orderId: id,
    version: z.number().int().positive(),
    section: z.string().min(1).max(200),
    designerId: id,
    deadline: timestampSchema,
    reason: z.string().trim().min(1).max(20000),
  })
  .strict()
/** The engineer marks a rework done; whoever asked for it may cancel it instead. */
export const engineeringReworkResolveSchema = versionedRecordSchema
  .extend({ status: z.enum(['done', 'cancelled']) })
  .strict()
export type EngineeringCreateInput = z.infer<typeof engineeringCreateSchema>
export type EngineeringEditInput = z.infer<typeof engineeringEditSchema>
